import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import ProductCard from './ProductCard';
import { DbProduct } from '../../utils/api';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface ProductCarouselProps {
  products: DbProduct[];
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({ products }) => {
  const carouselProducts = products.slice(0, 8);
  const mobileProducts = products.slice(0, 4);

  return (
    <div className="mt-1 mb-2 md:my-12 relative">
      <div className="flex items-center justify-between mb-1 md:mb-6">
        <div>
          <h2 className="text-base md:text-3xl font-bold text-gray-800 mb-0.5 md:mb-1">
            Neueste Artikel
          </h2>
          <p className="hidden md:block text-gray-600 text-base">
            Frisch eingestellt von Vereinsmitgliedern aus deiner Region
          </p>
        </div>
      </div>

      {/* Mobile: 2-Spalten Grid (4 Produkte = 2×2) */}
      <div data-testid="mobile-product-grid" className="grid grid-cols-2 gap-2 md:hidden">
        {mobileProducts.map((product) => (
          <Link key={product.id} to={`/products/${product.id}`} className="block">
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="overflow-hidden" style={{ maxHeight: '140px' }}>
                <img
                  src={product.image_url ? `${window.location.origin}${product.image_url}` : '/placeholder-image.jpg'}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  style={{ height: '140px' }}
                />
              </div>
              <div style={{ padding: '6px' }}>
                <p className="font-semibold text-gray-800 truncate leading-tight mb-0.5" style={{ fontSize: '12px' }}>
                  {product.title}
                </p>
                <p className="font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent" style={{ fontSize: '13px' }}>
                  {Number(product.price).toFixed(2)} €
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop: Swiper Karussell */}
      <div className="relative hidden md:block">
        <button className="swiper-btn-prev absolute left-0 top-1/2 -translate-y-8 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:bg-white transition-all hover:scale-110 hidden sm:flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button className="swiper-btn-next absolute right-0 top-1/2 -translate-y-8 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:bg-white transition-all hover:scale-110 hidden sm:flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={16}
          slidesPerView={1}
          navigation={{
            nextEl: '.swiper-btn-next',
            prevEl: '.swiper-btn-prev',
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={carouselProducts.length > 4}
          breakpoints={{
            640: { slidesPerView: 2, spaceBetween: 16 },
            768: { slidesPerView: 3, spaceBetween: 20 },
            1024: { slidesPerView: 4, spaceBetween: 24 },
          }}
          className="product-carousel !pb-12"
        >
          {carouselProducts.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCard {...product} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default ProductCarousel;
